import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileWarning, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { historicalConversionService } from '../../services';

const where = (t) => (t.local ? 'This server' : t.durable ? 'Durable store' : 'Missing');

export const SourceBindingTable = ({ binding }) => (
  <div className="rounded-md border bg-white p-3 text-xs" data-testid="source-binding-panel">
    <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
      <span data-testid="source-binding-fingerprint">Source fingerprint: <b>{binding.stage0_fingerprint}</b></span>
      <span data-testid="source-binding-build">Build: <b>{binding.build_commit || 'not recorded'}</b></span>
      <span data-testid="source-binding-schema">
        Schema: <b>{(binding.schema?.db_revision || []).join(', ') || 'unknown'}</b>
        {binding.schema?.code_head && ` (expected ${binding.schema.code_head}${binding.schema.ready ? ', OK' : ', NOT READY'})`}
      </span>
    </div>
    <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
      {Object.entries(binding.tables).map(([name, t]) => (
        <div key={name} className={`rounded px-2 py-1 ${t.local || t.durable ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`} data-testid={`source-binding-table-${name}`}>
          {name}: {where(t)}
        </div>
      ))}
    </div>
  </div>
);

export const SourceUnavailablePanel = ({ conversionId, detail, onRestored }) => {
  const [binding, setBinding] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    historicalConversionService.getSnapshotSourceBinding(conversionId).then(setBinding).catch(() => {});
  }, [conversionId]);

  const reattach = async () => {
    setBusy(true);
    try {
      await historicalConversionService.reattachSnapshotSource(conversionId, file);
      toast.success('Original source re-attached and verified');
      onRestored();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not re-attach the file');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="snapshot-source-unavailable">
      <Alert variant="destructive">
        <FileWarning className="h-4 w-4" />
        <AlertDescription>
          <strong>Original source file unavailable.</strong> {detail.message}
          <div className="mt-1 text-xs">Missing: {(detail.missing_tables || []).join(', ')} · fingerprint {detail.stage0_fingerprint}</div>
        </AlertDescription>
      </Alert>
      {binding && <SourceBindingTable binding={binding} />}
      <div className="rounded-md border p-4 text-sm" data-testid="snapshot-reattach-section">
        <p className="mb-2 text-slate-700">
          Re-attach the original .PSB file. It is only accepted if it is exactly the same file used for this import;
          matches, allowances, site records and audit history are not changed.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="file" accept=".PSB,.psb" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="snapshot-reattach-file-input" className="max-w-xs cursor-pointer" />
          <Button onClick={reattach} disabled={!file || busy} data-testid="snapshot-reattach-btn">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}Re-attach original file
          </Button>
        </div>
      </div>
    </div>
  );
};
